/**
 * Standard Actions configuration.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart}`) on every page — it is
 * there whether or not this file exists, and its built-in handlers are written
 * against stock Dawn's cart contract, which our drawer no longer resembles.
 *
 * Adapted from Dawn v16.0.0. Every deviation below is forced, not stylistic:
 *
 *   1. Upstream ends with `publish(PUB_SUB_EVENTS.cartUpdate, …)`. This theme
 *      forked before Dawn grew pubsub — no publish, no subscribers — so that
 *      call throws here. Dropped rather than remapped.
 *   2. Upstream skips the drawer body and the cart page, because its pubsub
 *      subscribers re-render them. Nothing re-renders them here, so there is no
 *      skip set and this file renders them itself.
 *   3. Upstream reads `${routes.cart_url}.js?sections=…`; that endpoint ignores
 *      the parameter, so upstream's render loop silently no-ops — see
 *      refreshCartUI.
 *   4. Upstream never reconciles `is-empty`, which sits on the <cart-drawer>
 *      parent of the mount it replaces — see reconcileEmptyState.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// <cart-notification> is deliberately absent: it only renders when
// settings.cart_type is "notification" and we run "drawer", so listing it would
// be code for a state this theme does not have.
const CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items'];

// Serialize refreshes. Two mutations in quick succession would otherwise race and
// the older render could land last. Same queue shape as cart.js:19,88.
let refreshQueue = Promise.resolve();

// How long to wait for <cart-drawer> to upgrade before giving up on a refresh.
// Only spent in the narrow window described in refreshCartUI, never on a warm page.
const UPGRADE_WAIT_MS = 2000;

// Walk every mounted cart custom element, collect the sections it wants rendered,
// and dedupe. Returns a Map keyed by section id, each entry pointing at the DOM
// mount and the selector used to extract the fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(CART_TAGS.join(','))) {
    let entries;
    try {
      // CartItems#getSectionsToRender reads #main-cart-items.dataset.id inline, so
      // it throws when that element is absent. Skipping the element is the right
      // outcome; this catch is load-bearing, not decorative.
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    for (const entry of entries ?? []) {
      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section id,
      //     entry.selector is a child node inside it.
      //   - cart-drawer style: entry.id IS the mount, with no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// `is-empty` gates whole regions in CSS — component-cart.css:7-15 hides
// .cart__contents and .cart__footer and shows .cart__empty-text. It lives on the
// <cart-drawer> / <cart-items> / #main-cart-footer wrappers, i.e. the PARENTS of
// every mount we replace, so re-rendering sections alone never clears it: adding
// the first item to an empty cart would render a full drawer and keep it hidden.
// It also has to happen alongside the render, not instead of it: CartDrawer.open()
// picks its focus-trap container off this class, reaching for .drawer__inner-empty
// while it is set — and a correct render has just removed that element.
// Mirrors cart.js:109-114.
function reconcileEmptyState(itemCount) {
  const isEmpty = itemCount === 0;
  document
    .querySelectorAll('cart-drawer, cart-items')
    .forEach((el) => el.classList.toggle('is-empty', isEmpty));
  const cartFooter = document.getElementById('main-cart-footer');
  if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
}

// After a Storefront API mutation, re-render every mounted cart section.
//
// Upstream fetches `${routes.cart_url}.js?sections=…` and reads
// `cartData.sections`. GET /cart.js ignores `sections` — 200, plain cart payload,
// no `sections` key — so upstream's loop iterates nothing and throws nothing. The
// Section Rendering API is not a substitute either: from a product URL it returns
// only that template's own sections, because the drawer is rendered from the
// layout. The Cart AJAX API renders arbitrary sections on request, which is how
// the rest of this theme already does it (cart.js:84-85, product-form.js).
async function refreshCartUI() {
  let sections = collectCartSections();

  if (!sections.size) {
    // Not "no cart elements on this page": <cart-drawer> renders on every page while
    // cart_type is "drawer" (theme.liquid:484). The real case is the pre-upgrade
    // window — the tags are in the DOM but cart-drawer.js (header.liquid:117, defer,
    // later in document order than this file) has not defined them yet, so
    // getSectionsToRender is still undefined. Wait for the upgrade, but bounded: if
    // that script is blocked outright it never arrives (see #689).
    await Promise.race([
      customElements.whenDefined('cart-drawer'),
      new Promise((resolve) => setTimeout(resolve, UPGRADE_WAIT_MS)),
    ]);
    sections = collectCartSections();
  }

  if (!sections.size) {
    // Deliberately logged rather than thrown. The caller reloads on a throw, and
    // reloading here would loop: the reload reproduces the same un-upgraded state,
    // so every agent retry would reload again.
    console.error('standard-actions: cart elements never upgraded; skipping refresh.');
    return;
  }

  const response = await fetch(routes.cart_update_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    // `sections`/`sections_url` only — with no other key this POST is a read. Any
    // key added here turns it into a mutation: `note` replaces, and `attributes`
    // deletes a key when its value is an empty string (cart-shipping-tier.js:56
    // relies on exactly that), so `_shipping_tier`, `_ad_consent` and `_bcai` are
    // one stray key away from being clobbered.
    body: JSON.stringify({
      sections: [...sections.keys()].join(','),
      sections_url: window.location.pathname,
    }),
  });

  if (!response.ok) throw new Error(`cart section render failed: HTTP ${response.status}`);
  const cart = await response.json();
  if (!cart.sections) throw new Error('cart section render returned no sections');

  let replacedDrawer = null;
  for (const [id, { mount, extractSelector }] of sections) {
    const html = cart.sections[id];
    if (!html) continue;
    const source = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(extractSelector);
    if (!source) continue;
    mount.replaceChildren(...source.childNodes);
    replacedDrawer = mount.closest('cart-drawer') ?? replacedDrawer;
  }

  reconcileEmptyState(cart.item_count);

  // Same reason as cart.js: this render path never goes through CartDrawer.open(),
  // so it is the only hook that reaches the deferred card images it just brought in.
  if (typeof window.hydrateCrossSellImages === 'function') window.hydrateCrossSellImages();

  // #CartDrawer-Overlay is a child of the mount we just replaced, so its
  // click-to-close listener went with the old node. cart-drawer.js:116 re-binds after
  // its own identical replacement; do the same rather than delegating from
  // <cart-drawer>, so there is one pattern here and no second listener racing the
  // theme's own on the nodes it renders.
  if (replacedDrawer) {
    const overlay = replacedDrawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', replacedDrawer.close.bind(replacedDrawer));
  }
}

function queueRefresh() {
  const next = refreshQueue.then(refreshCartUI, refreshCartUI);
  // Store a settled link so one failed refresh cannot wedge every later one.
  refreshQueue = next.catch(() => {});
  return next;
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  // updateCart is configured first because it is the half that carries the fix,
  // and the bundle is an external boundary: if openCart were absent, configuring
  // it would throw and take updateCart down with it.
  if (actions.updateCart) {
    actions.updateCart.configure({
      // The bundle requires an eventTarget; document is the conventional root.
      eventTarget: () => document,
      async handler(defaultHandler) {
        const result = await defaultHandler();
        try {
          await queueRefresh();
        } catch (error) {
          console.error('standard-actions: cart refresh failed; reloading.', error);
          window.location.reload();
        }
        return result;
      },
    });
  }

  if (actions.openCart) {
    actions.openCart.configure({
      async handler(defaultHandler) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
          return;
        }
        return defaultHandler();
      },
    });
  }
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after all
// module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}
